import { IToothDetails } from '@dataTypes/appointment.type'
import { IToothSurface } from '@dataTypes/treatmentPlan.types'
import { formatToothDetailsToothLabel } from '@utils/procedure-surface-selection.utils'

export const buildPresetToothDetails = (toothNum: number): IToothDetails[] => [
  { area_type: 1, tooth_num: toothNum },
]

export const buildPresetToothDetailsForTeeth = (
  toothNums: number[],
): IToothDetails[] =>
  [...toothNums]
    .sort((a, b) => a - b)
    .map((toothNum) => ({ area_type: 1, tooth_num: toothNum }))

const formatToothNumRanges = (sortedToothNums: number[]): string => {
  const ranges: string[] = []
  let rangeStart = sortedToothNums[0]
  let rangeEnd = sortedToothNums[0]
  for (let index = 1; index < sortedToothNums.length; index += 1) {
    const toothNum = sortedToothNums[index]
    if (toothNum === rangeEnd + 1) {
      rangeEnd = toothNum
      continue
    }
    ranges.push(
      rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}–${rangeEnd}`,
    )
    rangeStart = toothNum
    rangeEnd = toothNum
  }
  ranges.push(
    rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}–${rangeEnd}`,
  )
  return ranges.join(', ')
}

export const formatPresetToothNumsLabel = (toothNums: number[]): string => {
  const sorted = [...toothNums].sort((a, b) => a - b)
  if (sorted.length === 0) {
    return ''
  }
  if (sorted.length === 1) {
    return `Tooth ${sorted[0]}`
  }
  return `Teeth ${formatToothNumRanges(sorted)}`
}

const isToothSurfaceDetail = (detail: IToothDetails): boolean => {
  const areaType = detail.area_type ?? 1
  return (
    areaType === 1 &&
    detail.tooth_num !== undefined &&
    detail.tooth_num !== null
  )
}

/** One group per tooth; arch/quadrant/mouth/none stay on a single procedure line. */
export const splitToothDetailsByTooth = (
  toothDetails: IToothDetails[],
): IToothDetails[][] => {
  if (toothDetails.length === 0) {
    return [[]]
  }
  const hasNonToothArea = toothDetails.some(
    (detail) => !isToothSurfaceDetail(detail),
  )
  if (hasNonToothArea) {
    return [toothDetails]
  }
  const byTooth = new Map<number, IToothDetails>()
  for (const detail of toothDetails) {
    const toothNum = detail.tooth_num as number
    const existing = byTooth.get(toothNum)
    if (!existing) {
      byTooth.set(toothNum, {
        ...detail,
        surf: detail.surf ? [...detail.surf] : detail.surf,
      })
      continue
    }
    const mergedSurf = [...(existing.surf ?? [])]
    for (const surface of detail.surf ?? []) {
      if (!mergedSurf.includes(surface)) {
        mergedSurf.push(surface)
      }
    }
    if (mergedSurf.length > 0) {
      existing.surf = mergedSurf
    }
  }
  if (byTooth.size <= 1) {
    return [toothDetails]
  }
  return Array.from(byTooth.values()).map((detail) => [detail])
}

export const formatToothDetailsToSurfaceString = (
  toothDetails: IToothDetails[],
): string => {
  if (toothDetails.length === 0) {
    return ''
  }
  if (toothDetails[0].area && !toothDetails[0].tooth_num) {
    return toothDetails[0].area
  }
  return toothDetails
    .map((detail) => {
      if (detail.tooth_num !== undefined) {
        return formatToothDetailsToothLabel(detail)
      }
      return ''
    })
    .filter((s) => s)
    .join(', ')
}

export const convertToothDetailsToToothSurfaces = (
  toothDetails: IToothDetails[],
  itemId: string,
): IToothSurface[] => {
  if (toothDetails.length === 0) {
    return []
  }
  const result: IToothSurface[] = []
  let counter = 0
  toothDetails.forEach((detail) => {
    if (detail.area && !detail.tooth_num) {
      result.push({
        ts_id: `ts-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${counter++}`,
        item_id: itemId,
        area_type: detail.area_type ?? null,
        area: detail.area,
        tooth: null,
        surf: null,
      })
    } else if (detail.tooth_num !== undefined) {
      const toothNum = detail.tooth_num
      if (detail.surf && detail.surf.length > 0) {
        detail.surf.forEach((surface) => {
          result.push({
            ts_id: `ts-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${counter++}`,
            item_id: itemId,
            area_type: detail.area_type ?? 1,
            area: null,
            tooth: toothNum.toString(),
            surf: surface,
          })
        })
      } else {
        result.push({
          ts_id: `ts-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${counter++}`,
          item_id: itemId,
          area_type: detail.area_type ?? 1,
          area: null,
          tooth: toothNum.toString(),
          surf: null,
        })
      }
    }
  })
  return result
}
